import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { StyleSheet, css } from 'aphrodite';
import { useNavigate } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { CircularProgress } from '@mui/material';
import { CheckCircle, Error } from '@mui/icons-material';
import { toast, ToastContainer } from 'react-toastify';
import { showToast, toTitleCase } from '../../../utils/user/patient/helpers';
import { updatePatientData } from '../../../redux/middleware/user/patient/patient_data';

// Component imports
import ProfilePicture from '../../../scenes/user/patient/profile_sections/ProfilePicture';
import PersonalInformation from '../../../scenes/user/patient/profile_sections/PersonalInformation';
import ContactInformation from '../../../scenes/user/patient/profile_sections/ContactInformation';
import EmergencyContact from '../../../scenes/user/patient/profile_sections/EmergencyContact';
import MedicalInformation from '../../../scenes/user/patient/profile_sections/MedicalInformation';
import Preferences from '../../../scenes/user/patient/profile_sections/Preferences';
import InsuranceInformation from '../../../scenes/user/patient/profile_sections/InsuranceInformation';
import Summary from '../../../scenes/user/patient/profile_sections/Summary';
import { usePatientAuth } from '../../../components/patient/hooks/usePatientAuth';
import { TOASTIFY_NEWEST_ON_TOP } from '../../../utils/user/patient/config';

const tabs = [
  { id: 'profile', label: 'Profile Picture' },
  { id: 'personal', label: 'Personal Info' },
  { id: 'contact', label: 'Contact Info' },
  { id: 'emergency', label: 'Emergency Contact' },
  { id: 'medical', label: 'Medical Info' },
  { id: 'preferences', label: 'Preferences' },
  { id: 'insurance', label: 'Insurance' },
  { id: 'summary', label: 'Summary' }
];

const Page = ({ preFilledData }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Validate authorization
  usePatientAuth(false, true);

  const { loading, error } = useSelector((state) => state.patientData);

  const [activeTab, setActiveTab] = useState('profile');
  const [formData, setFormData] = useState({
    profilePicture: null,
    personalInfo: { ...preFilledData },
    contactInfo: { ...preFilledData },
    emergencyContact: {},
    medicalInfo: {},
    preferences: {},
    insuranceInfo: {}
  });

  const [sectionStatus, setSectionStatus] = useState({
    profile: { isComplete: false, hasError: false },
    personal: { isComplete: false, hasError: false },
    contact: { isComplete: false, hasError: false },
    emergency: { isComplete: false, hasError: false },
    medical: { isComplete: false, hasError: false },
    preferences: { isComplete: false, hasError: false },
    insurance: { isComplete: false, hasError: false },
    summary: { isComplete: false, hasError: false }
  });

  const [completionPercentage, setCompletionPercentage] = useState(0);
  const tabsRef = useRef(null);

  const calculateCompletionPercentage = useCallback(() => {
    const requiredSections = [
      'profile',
      'personal',
      'contact',
      'emergency',
      'medical',
      'preferences',
      'insurance'
    ];
    const completedSections = requiredSections.filter(
      (section) =>
        sectionStatus[section].isComplete && !sectionStatus[section].hasError
    ).length;
    setCompletionPercentage(
      (completedSections / requiredSections.length) * 100
    );
  }, [sectionStatus]);

  useEffect(() => {
    calculateCompletionPercentage();
  }, [calculateCompletionPercentage]);

  const handleSectionComplete = (
    sectionName,
    sectionFormName,
    sectionData,
    isValid
  ) => {
    setFormData((prevData) => ({
      ...prevData,
      [sectionFormName]: sectionData
    }));
    setSectionStatus((prevStatus) => ({
      ...prevStatus,
      [sectionName]: { isComplete: true, hasError: !isValid }
    }));
    if (isValid) {
      moveToNextSection();
    }
  };

  const isProfileComplete = useCallback(() => {
    const requiredSections = [
      'profile',
      'personal',
      'contact',
      'emergency',
      'medical',
      'preferences',
      'insurance'
    ];
    return requiredSections.every(
      (section) =>
        sectionStatus[section].isComplete && !sectionStatus[section].hasError
    );
  }, [sectionStatus]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('Profile submitted:', formData);

    try {
      // Helper to handle optional fields
      const getValue = (obj, path, defaultValue = '') => {
        return obj && obj[path] ? obj[path] : defaultValue;
      };

      // Helper to handle array fields
      const getArrayValue = (obj, path) => {
        return obj && obj[path] && Array.isArray(obj[path])
          ? obj[path].filter((item) => item && item.trim() !== '')
          : [];
      };

      // Flatten data structure with optional field handling
      const flattenedData = {
        // Personal Info - Required fields
        first_name: toTitleCase(getValue(formData.personalInfo, 'firstName')),
        last_name: toTitleCase(getValue(formData.personalInfo, 'lastName')),
        date_of_birth: getValue(formData.personalInfo, 'dateOfBirth'),
        gender: getValue(formData.personalInfo, 'gender', '').toUpperCase(),

        // Contact Info - Optional fields
        phone_number: getValue(formData.contactInfo, 'phoneNumber'),
        alternate_phone_number: getValue(
          formData.contactInfo,
          'alternatePhoneNumber'
        ),
        country: toTitleCase(getValue(formData.contactInfo, 'country')),
        state_province: toTitleCase(getValue(formData.contactInfo, 'state')),
        city: toTitleCase(getValue(formData.contactInfo, 'city')),
        street_address: toTitleCase(
          getValue(formData.contactInfo, 'streetAddress')
        ),
        postal_code: getValue(formData.contactInfo, 'postalCode'),

        // Emergency Contact - Optional fields
        emergency_contact_name: toTitleCase(
          getValue(formData.emergencyContact, 'name')
        ),
        emergency_contact_relationship: getValue(
          formData.emergencyContact,
          'relationship'
        ).toUpperCase(),
        emergency_contact_phone: getValue(
          formData.emergencyContact,
          'phoneNumber'
        ),
        emergency_contact_email: getValue(formData.emergencyContact, 'email'),

        // Medical Info - Lists and optional fields
        blood_group: getValue(formData.medicalInfo, 'bloodGroup').toUpperCase(),
        known_allergies: getArrayValue(formData.medicalInfo, 'allergies').map(
          toTitleCase
        ),
        chronic_conditions: getArrayValue(
          formData.medicalInfo,
          'chronicConditions'
        ).map(toTitleCase),
        current_medications: getArrayValue(
          formData.medicalInfo,
          'currentMedications'
        ).map(toTitleCase),
        primary_physician: toTitleCase(
          getValue(formData.medicalInfo, 'primaryPhysician')
        ),

        // Preferences - Optional fields
        preferred_language: toTitleCase(
          getValue(formData.preferences, 'preferredLanguage')
        ),
        notification_preferences: getValue(
          formData.preferences,
          'notificationPreferences'
        ),

        // Insurance Info - Optional fields
        insurance_name: toTitleCase(
          getValue(formData.insuranceInfo, 'insuranceProvider')
        ),
        insurance_number: getValue(formData.insuranceInfo, 'policyNumber'),
        insurance_expiry: getValue(formData.insuranceInfo, 'expiryDate')
      };

      // Remove empty values
      const cleanedData = Object.fromEntries(
        Object.entries(flattenedData).filter(
          ([_, value]) =>
            value !== '' &&
						value !== null &&
						value !== undefined &&
						(Array.isArray(value) ? value.length > 0 : true)
        )
      );

      // Create FormData
      const formDataToSend = new FormData();

      // Handle optional profile picture
      if (formData.profilePicture) {
        formDataToSend.append('profile_picture', formData.profilePicture);
      }

      // Append cleaned data
      formDataToSend.append('data', JSON.stringify(cleanedData));

      const response = await dispatch(updatePatientData(formDataToSend));

      if (response.status === 200) {
        showToast(toast, 'success', 'Profile updated successfully');
        navigate('/app/user/dashboard');
      } else {
        showToast(
          toast,
          'error',
          response.data?.detail || 'Unexpected response from server'
        );
      }
    } catch (error) {
      console.error('Error submitting profile:', error);
      // Show error message to user
      showToast(
        toast,
        'error',
        error.response?.data?.message ||
					'There was an error updating your profile.'
      );
    }
  };

  const scrollToTab = (tabId) => {
    const tabElement = document.getElementById(`tab-${tabId}`);
    if (tabElement && tabsRef.current) {
      const scrollLeft =
				tabElement.offsetLeft -
				tabsRef.current.clientWidth / 2 +
				tabElement.clientWidth / 2;
      tabsRef.current.scrollTo({ left: scrollLeft, behavior: 'smooth' });
    }
  };

  useEffect(() => {
    scrollToTab(activeTab);
  }, [activeTab, scrollToTab]); // Added scrollToTab to dependencies

  const moveToNextSection = () => {
    const currentIndex = tabs.findIndex((tab) => tab.id === activeTab);
    if (currentIndex < tabs.length - 1) {
      setActiveTab(tabs[currentIndex + 1].id);
    }
  };

  const moveToPreviousSection = () => {
    const currentIndex = tabs.findIndex((tab) => tab.id === activeTab);
    if (currentIndex > 0) {
      setActiveTab(tabs[currentIndex - 1].id);
    }
  };

  const canNavigateToSection = (tabId) => {
    const tabIndex = tabs.findIndex((tab) => tab.id === tabId);
    return tabs
      .slice(0, tabIndex)
      .every(
        (tab) =>
          sectionStatus[tab.id].isComplete && !sectionStatus[tab.id].hasError
      );
  };

  return (
    <div className={css(styles.container)}>
      <div className={css(styles.contentWrapper)}>
        <h1 className={css(styles.title)}>Complete Your Profile</h1>
        <div className={css(styles.progressBarContainer)}>
          <motion.div
            className={css(styles.progressBar)}
            initial={{ width: 0 }}
            animate={{ width: `${completionPercentage}%` }}
            transition={{ duration: 0.5 }}
          />
        </div>
        <p className={css(styles.progressText)}>
          {`${Math.round(completionPercentage)}% Complete`}
        </p>

        <div className={css(styles.tabContainer)} ref={tabsRef}>
          {tabs.map((tab, index) => (
            <motion.button
              key={tab.id}
              id={`tab-${tab.id}`}
              className={css(
							  styles.tabButton,
							  activeTab === tab.id && styles.activeTab,
							  sectionStatus[tab.id].isComplete &&
									!sectionStatus[tab.id].hasError &&
									styles.completedTab,
							  !canNavigateToSection(tab.id) && styles.disabledTab
              )}
              onClick={() =>
							  canNavigateToSection(tab.id) && setActiveTab(tab.id)}
              whileHover={canNavigateToSection(tab.id) ? { scale: 1.05 } : {}}
              whileTap={canNavigateToSection(tab.id) ? { scale: 0.95 } : {}}
              disabled={!canNavigateToSection(tab.id)}
            >
              <span className={css(styles.tabNumber)}>{index + 1}</span>
              <span className={css(styles.tabLabel)}>{tab.label}</span>
              {sectionStatus[tab.id].isComplete &&
								!sectionStatus[tab.id].hasError && (
  <CheckCircle className={css(styles.completedIcon)} />
              )}
              {sectionStatus[tab.id].hasError && (
                <Error className={css(styles.errorIcon)} />
              )}
              {activeTab === tab.id && (
                <motion.div
                  className={css(styles.activeTabIndicator)}
                  layoutId='activeTabIndicator'
                />
              )}
            </motion.button>
          ))}
        </div>

        <AnimatePresence mode='wait'>
          <motion.div
            key={activeTab}
            className={css(styles.tabContent)}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            {activeTab === 'profile' && (
              <ProfilePicture
                initialData={formData.profilePicture}
                onComplete={(data, isValid) => {
								  handleSectionComplete(
								    'profile',
								    'profilePicture',
								    data,
								    isValid
								  );
                }}
              />
            )}
            {activeTab === 'personal' && (
              <PersonalInformation
                initialData={formData.personalInfo}
                onComplete={(data, isValid) => {
								  handleSectionComplete(
								    'personal',
								    'personalInfo',
								    data,
								    isValid
								  );
                }}
                onBack={moveToPreviousSection}
              />
            )}
            {activeTab === 'contact' && (
              <ContactInformation
                initialData={formData.contactInfo}
                onComplete={(data, isValid) => {
								  handleSectionComplete(
								    'contact',
								    'contactInfo',
								    data,
								    isValid
								  );
                }}
                onBack={moveToPreviousSection}
              />
            )}
            {activeTab === 'emergency' && (
              <EmergencyContact
                initialData={formData.emergencyContact}
                onComplete={(data, isValid) => {
								  handleSectionComplete(
								    'emergency',
								    'emergencyContact',
								    data,
								    isValid
								  );
                }}
                onBack={moveToPreviousSection}
              />
            )}
            {activeTab === 'medical' && (
              <MedicalInformation
                initialData={formData.medicalInfo}
                onComplete={(data, isValid) => {
								  handleSectionComplete(
								    'medical',
								    'medicalInfo',
								    data,
								    isValid
								  );
                }}
                onBack={moveToPreviousSection}
              />
            )}
            {activeTab === 'preferences' && (
              <Preferences
                initialData={formData.preferences}
                onComplete={(data, isValid) => {
								  handleSectionComplete(
								    'preferences',
								    'preferences',
								    data,
								    isValid
								  );
                }}
                onBack={moveToPreviousSection}
              />
            )}
            {activeTab === 'insurance' && (
              <InsuranceInformation
                initialData={formData.insuranceInfo}
                onComplete={(data, isValid) => {
								  handleSectionComplete(
								    'insurance',
								    'insuranceInfo',
								    data,
								    isValid
								  );
                }}
                onBack={moveToPreviousSection}
              />
            )}
            {activeTab === 'summary' && (
              <Summary
                formData={formData}
                onBack={moveToPreviousSection}
                onSubmit={handleSubmit}
              />
            )}
          </motion.div>
        </AnimatePresence>
        <motion.button
          className={css(
					  styles.submitButton,
					  !isProfileComplete() && styles.disabledButton
          )}
          onClick={handleSubmit}
          whileHover={isProfileComplete() ? { scale: 1.05 } : {}}
          whileTap={isProfileComplete() ? { scale: 0.95 } : {}}
          disabled={!isProfileComplete()}
        >
          {loading
            ? (
              <CircularProgress size={24} color='inherit' />
              )
            : (
					  'Submit Profile'
              )}
        </motion.button>
      </div>
      {/* <ToastContainer newestOnTop={TOASTIFY_NEWEST_ON_TOP} /> */}
    </div>
  );
};

const styles = StyleSheet.create({
  container: {
    minHeight: '100vh',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#f0f4f8',
    padding: '1rem',
    boxSizing: 'border-box'
  },
  contentWrapper: {
    width: '100%',
    maxWidth: '800px',
    backgroundColor: '#ffffff',
    borderRadius: '16px',
    boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
    overflow: 'hidden'
  },
  title: {
    fontSize: '2rem',
    textAlign: 'center',
    margin: '2rem 0',
    color: '#2783F7',
    fontWeight: 'bold'
  },
  progressBarContainer: {
    height: '6px',
    backgroundColor: '#e2e8f0',
    borderRadius: '3px',
    overflow: 'hidden',
    margin: '0 2rem 1rem'
  },
  progressBar: {
    height: '100%',
    backgroundColor: '#2783F7',
    borderRadius: '3px',
    transition: 'width 0.5s ease-in-out'
  },
  progressText: {
    textAlign: 'center',
    marginBottom: '1.5rem',
    fontSize: '0.875rem',
    color: '#4a5568',
    fontWeight: '500'
  },
  tabContainer: {
    display: 'flex',
    overflowX: 'auto',
    scrollbarWidth: 'none',
    msOverflowStyle: 'none',
    '::-webkit-scrollbar': {
      display: 'none'
    },
    padding: '0.5rem 1rem',
    margin: '0 1rem 1.5rem',
    backgroundColor: '#f8fafc',
    borderRadius: '8px'
  },
  tabButton: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '0.75rem 1rem',
    backgroundColor: 'transparent',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '0.875rem',
    color: '#4a5568',
    transition: 'all 0.2s ease-in-out',
    position: 'relative',
    flexShrink: 0,
    ':hover': {
      backgroundColor: 'rgba(39, 131, 247, 0.1)'
    }
  },
  activeTab: {
    color: '#2783F7',
    fontWeight: '600',
    backgroundColor: 'rgba(39, 131, 247, 0.1)'
  },
  completedTab: {
    color: '#48bb78'
  },
  disabledTab: {
    opacity: 0.5,
    cursor: 'not-allowed'
  },
  tabNumber: {
    fontSize: '1rem',
    fontWeight: 'bold',
    marginBottom: '0.25rem'
  },
  tabLabel: {
    fontSize: '0.75rem',
    whiteSpace: 'nowrap'
  },
  activeTabIndicator: {
    position: 'absolute',
    bottom: '-0.5rem',
    left: '0',
    right: '0',
    height: '3px',
    backgroundColor: '#2783F7',
    borderRadius: '1.5px'
  },
  completedIcon: {
    position: 'absolute',
    top: '0.25rem',
    right: '0.25rem',
    color: '#48bb78',
    fontSize: '1rem'
  },
  errorIcon: {
    position: 'absolute',
    top: '0.25rem',
    right: '0.25rem',
    color: '#e53e3e',
    fontSize: '1rem'
  },
  tabContent: {
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    padding: '1.5rem',
    margin: '0 1rem 1.5rem',
    boxShadow: 'inset 0 2px 4px rgba(0, 0, 0, 0.05)'
  },
  submitButton: {
    backgroundColor: '#2783F7',
    color: 'white',
    border: 'none',
    padding: '0.75rem 1rem',
    fontSize: '1rem',
    fontWeight: '600',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s ease-in-out',
    boxShadow: '0 4px 6px rgba(39, 131, 247, 0.2)',
    flex: '0 0 auto',
    width: '100%',
    ':hover': {
      backgroundColor: '#1c6ed8',
      transform: 'translateY(-2px)',
      boxShadow: '0 6px 8px rgba(39, 131, 247, 0.3)'
    }
  },
  disabledButton: {
    backgroundColor: '#a0aec0',
    cursor: 'not-allowed',
    ':hover': {
      backgroundColor: '#a0aec0',
      transform: 'none',
      boxShadow: 'none'
    }
  },
  '@media (max-width: 768px)': {
    container: {
      padding: '0.5rem'
    },
    title: {
      fontSize: '1.5rem',
      margin: '1.5rem 0'
    },
    progressBarContainer: {
      margin: '0 1rem 0.75rem'
    },
    progressText: {
      marginBottom: '1rem'
    },
    tabContainer: {
      margin: '0 0.5rem 1rem'
    },
    tabContent: {
      padding: '1rem',
      margin: '0 0.5rem 1rem'
    },
    submitButton: {
      maxWidth: '100%',
      padding: '0.75rem 1rem',
      margin: '1rem auto'
    }
  }
});

export default Page;
